/**
 * Storage service for multiple builds.
 * Single source of truth: dofgen_builds array + dofgen_active_build_id
 */
import Build from '../core/Build';
import GenConfig from '../core/GenConfig';
import { getGenDefaults } from './genDefaults';
// Runtime-only cycle: anonSync reads and writes entries through this module so
// dofgen_builds keeps a single writer, and this module only calls back into it
// from inside function bodies, never at import time.
import { retractAnonBuild } from './anonSync';
import { relativizeMediaUrls } from '../utils/mediaUrl';
import { pruneCriteriaWeights } from '../core/criteriaWeights';

const KEY_BUILDS = 'dofgen_builds';
const KEY_ACTIVE_ID = 'dofgen_active_build_id';
const KEY_ACTIVE_MENU = 'dofgen_generator_active_menu';
/** One-shot handoff so a newly opened tab can load a specific local build. */
const KEY_BOOT_BUILD_ID = 'dofgen_boot_build_id';

// Legacy keys for migration
const LEGACY_BUILD = 'dofgen_generator_build';
const LEGACY_BUILD_NAME = 'dofgen_generator_build_name';
const LEGACY_GEN_CONFIG = 'dofgen_generator_config';

const DEFAULT_LVL = 200;
const DEFAULT_NAME = '';

// Matches a leading copy counter like "[3] " so repeated copies increment the
// number instead of stacking " (copy)" suffixes.
const COPY_PREFIX_RE = /^\[(\d+)\]\s*(.*)$/;

/**
 * Builds the name for a duplicated build: "Original name" -> "[2] Original name",
 * "[2] Original name" -> "[3] Original name".
 */
export function duplicateName(name) {
    const trimmed = (name || '').trim();
    const match = trimmed.match(COPY_PREFIX_RE);
    if (match) {
        const next = parseInt(match[1], 10) + 1;
        return `[${next}] ${match[2]}`.trim();
    }
    return `[2] ${trimmed}`.trim();
}

function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Compact final stat sheet ({ statId: roundedValue }) persisted with the build so
 * the server-side OG card can show real values without recomputing getStats().
 * Lang-agnostic (numbers only); labels/icons are resolved server-side.
 */
function buildStatSheet(build) {
    if (!build || typeof build.getStats !== 'function') return null;
    let stats;
    try {
        stats = build.getStats() || {};
    } catch (e) {
        return null;
    }
    const sheet = {};
    for (const [id, entry] of Object.entries(stats)) {
        const v = Number(entry?.current);
        if (Number.isFinite(v) && v !== 0) sheet[id] = Math.round(v);
    }
    return Object.keys(sheet).length ? sheet : null;
}

function buildToStorage(build) {
    if (!build) return null;
    // Item icons arrive already pointed at whatever media host is current, and
    // this is where they get frozen. Store the path only: builds outlive media
    // hosts, and the ones saved before the R2 move lost their icons this way.
    return relativizeMediaUrls({
        statSheet: buildStatSheet(build),
        lvl: build.lvl,
        // Item *ids*, not snapshots. Sixteen stored items were 44KB of a 50KB
        // build and the single largest thing the database shipped; the browser
        // holds the catalog now and resolves them on read (see core/Build).
        //
        // Taken from `itemIds` rather than from `items`, because that is what
        // survives when the catalog has not loaded: a build opened before it
        // arrives still round-trips its equipment instead of saving sixteen
        // empty slots over it.
        items: build.itemIds || build.items.map((i) => (i && i.id) || null),
        // `setBonuses` is deliberately not stored. It is a pure function of the
        // items above and the catalog, and storing it meant shipping every
        // member item of every active set — 5.3 KB a build, the last large
        // thing in the payload. core/Build derives it on read.
        attributes: build.attributes,
        hasScrolls: build.scrolls && Object.values(build.scrolls).some(v => v > 0),
        scrolls: build.scrolls,
        desired: build.desired,
        desiredVersion: build.desiredVersion,
        maximizeTargets: build.maximizeTargets || {},
        maximizeSpellIds: build.maximizeSpellIds || [],
        maximizeSpellCriteria: build.maximizeSpellCriteria || [],
        maximizeWeapon: !!build.maximizeWeapon,
        maximizeBestWeapon: !!build.maximizeBestWeapon,
        bestWeaponMetric: build.bestWeaponMetric,
        weaponMagingElement: build.weaponMagingElement ?? null,
        weaponMagingHealElement: build.weaponMagingHealElement ?? null,
        // Overmage/exo lines per slot, each carrying the item id it belongs to.
        itemMaging: build.itemMaging || {},
        // Transcendence runes in generations: the user's choice, or null while
        // it still follows the level (Build.isTranscendenceOn).
        transcendence: build.transcendence ?? null,
        // Exos generations place on items (the forgemagie menu), apart from
        // `exos`, the stats panel's shortcuts.
        fmExos: build.fmExos,
        // Option keys only (`{ weapon_mastery: ['103-crit'] }`); what they are
        // worth is re-derived from the build's level — see core/boosts.js.
        boosts: build.boosts || {},
        // `{ boostKey: n }` for boosts held several times; absent means one.
        boostCharges: build.boostCharges || {},
        // `{ spellId: { lineKey: n } }` for charged lines read below their maximum.
        spellCharges: build.spellCharges || {},
        characterClass: build.characterClass,
        characterSex: typeof build.getCharacterSex === 'function' ? build.getCharacterSex() : (build.characterSex || 0),
        characterRender: typeof build.getCharacterRender === 'function' ? build.getCharacterRender() : (build.characterRender || null),
        // How the character was last being looked at, so the build reopens that
        // way. Both are the renderer's vocabulary and are re-validated against
        // it on the way in, so an old value cannot break a build.
        characterStance: typeof build.getCharacterStance === 'function' ? build.getCharacterStance() : (build.characterStance ?? null),
        characterFacing: typeof build.getCharacterFacing === 'function' ? build.getCharacterFacing() : (build.characterFacing ?? null),
        criteriaTags: build.criteriaTags || [],
        // Per-criterion weight multipliers, pruned here: criteria edits are
        // sometimes two builds long, so pruning any earlier would drop a weight
        // mid-move (see core/criteriaWeights.pruneCriteriaWeights).
        criteriaWeights: pruneCriteriaWeights(build),
        // Written so community cards can tint themselves without being sent the
        // equipment this is derived from. Recomputed here on every save, so it
        // cannot drift from the items; see Build.getElementTint.
        elementTint: typeof build.getElementTint === 'function' ? build.getElementTint() : null,
    });
}

function migrateFromLegacy() {
    try {
        const legacyBuild = localStorage.getItem(LEGACY_BUILD);
        if (!legacyBuild) return false;

        const buildData = JSON.parse(legacyBuild);
        const name = localStorage.getItem(LEGACY_BUILD_NAME) || DEFAULT_NAME;
        let genConfig = {};
        const savedConfig = localStorage.getItem(LEGACY_GEN_CONFIG);
        if (savedConfig) {
            genConfig = JSON.parse(savedConfig);
        }

        const id = uuid();
        const now = new Date().toISOString();
        const entry = {
            id,
            name,
            build: buildData,
            genConfig,
            createdAt: now,
            updatedAt: now
        };

        localStorage.setItem(KEY_BUILDS, JSON.stringify([entry]));
        localStorage.setItem(KEY_ACTIVE_ID, id);
        localStorage.removeItem(LEGACY_BUILD);
        localStorage.removeItem(LEGACY_BUILD_NAME);
        localStorage.removeItem(LEGACY_GEN_CONFIG);
        return true;
    } catch (e) {
        console.warn('Migration failed:', e);
        return false;
    }
}

export function getBuilds() {
    try {
        let raw = localStorage.getItem(KEY_BUILDS);
        if (!raw) {
            if (migrateFromLegacy()) {
                raw = localStorage.getItem(KEY_BUILDS);
            }
        }
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr : [];
    } catch (e) {
        console.warn('getBuilds failed:', e);
        return [];
    }
}

export function getActiveBuildId() {
    try {
        return localStorage.getItem(KEY_ACTIVE_ID);
    } catch (e) {
        return null;
    }
}

export function setActiveBuildId(id) {
    try {
        if (id) {
            localStorage.setItem(KEY_ACTIVE_ID, id);
        } else {
            localStorage.removeItem(KEY_ACTIVE_ID);
        }
    } catch (e) {
        console.warn('setActiveBuildId failed:', e);
    }
}

export function setBootBuildId(id) {
    try {
        if (id) localStorage.setItem(KEY_BOOT_BUILD_ID, id);
        else localStorage.removeItem(KEY_BOOT_BUILD_ID);
    } catch (e) {
        console.warn('setBootBuildId failed:', e);
    }
}

/** Read and clear the pending boot build id (cross-tab local-build handoff). */
export function consumeBootBuildId() {
    try {
        const id = localStorage.getItem(KEY_BOOT_BUILD_ID);
        if (id) localStorage.removeItem(KEY_BOOT_BUILD_ID);
        return id;
    } catch (e) {
        return null;
    }
}

export function getBuild(id) {
    const builds = getBuilds();
    return builds.find(b => b.id === id) || null;
}

function persistBuilds(builds) {
    try {
        localStorage.setItem(KEY_BUILDS, JSON.stringify(builds));
    } catch (e) {
        console.warn('persistBuilds failed:', e);
    }
}

export function createBuild() {
    const builds = getBuilds();
    const id = uuid();
    const now = new Date().toISOString();
    const buildData = { lvl: DEFAULT_LVL, items: Array(16).fill(null), characterSex: 1, criteriaTags: [] };
    const entry = {
        id,
        name: DEFAULT_NAME,
        build: buildData,
        genConfig: getGenDefaults(),
        createdAt: now,
        updatedAt: now
    };
    builds.push(entry);
    persistBuilds(builds);
    setActiveBuildId(id);
    return entry;
}

export function createBuildFromSavedPayload({ name, build, genConfig }) {
    const builds = getBuilds();
    const id = uuid();
    const now = new Date().toISOString();
    const entry = {
        id,
        name: name || DEFAULT_NAME,
        build: build || { lvl: DEFAULT_LVL, items: Array(16).fill(null), characterSex: 1, criteriaTags: [] },
        genConfig: genConfig && typeof genConfig?.toObject === 'function' ? genConfig.toObject() : (genConfig || {}),
        createdAt: now,
        updatedAt: now,
    };
    builds.push(entry);
    persistBuilds(builds);
    setActiveBuildId(id);
    return entry;
}

export function updateBuild(id, { build, name, genConfig }) {
    const builds = getBuilds();
    const idx = builds.findIndex(b => b.id === id);
    if (idx < 0) return null;
    const entry = { ...builds[idx], updatedAt: new Date().toISOString() };
    if (build !== undefined) entry.build = buildToStorage(build);
    if (name !== undefined) entry.name = name;
    // Same host-freezing hazard as buildToStorage: lockedSlots and blacklist
    // hold whole items, icons included.
    if (genConfig !== undefined) entry.genConfig = relativizeMediaUrls(
        genConfig && typeof genConfig.toObject === 'function' ? genConfig.toObject() : genConfig
    );
    builds[idx] = entry;
    persistBuilds(builds);
    return entry;
}

/**
 * Record where a build landed in the community after a sync. Deliberately does
 * not touch updatedAt or reschedule a sync — doing either would loop.
 */
export function setBuildPublication(id, { publicCode, ownerSlug }) {
    const builds = getBuilds();
    const idx = builds.findIndex(b => b.id === id);
    if (idx < 0) return null;
    const entry = { ...builds[idx], publicCode: publicCode || null, ownerSlug: ownerSlug || null };
    builds[idx] = entry;
    persistBuilds(builds);
    return entry;
}

export function deleteBuild(id) {
    const builds = getBuilds();
    const filtered = builds.filter(b => b.id !== id);
    if (filtered.length === builds.length) return builds;
    retractAnonBuild(builds.find(b => b.id === id));
    persistBuilds(filtered);
    const activeId = getActiveBuildId();
    if (activeId === id) {
        const next = filtered[0];
        setActiveBuildId(next ? next.id : null);
    }
    return filtered;
}

export function duplicateBuild(id) {
    const source = getBuild(id);
    if (!source) return null;
    const builds = getBuilds();
    const newId = uuid();
    const now = new Date().toISOString();
    const entry = {
        id: newId,
        name: duplicateName(source.name),
        build: { ...source.build },
        genConfig: { ...source.genConfig },
        createdAt: now,
        updatedAt: now
    };
    builds.push(entry);
    persistBuilds(builds);
    setActiveBuildId(newId);
    return entry;
}

export function getActiveMenu() {
    try {
        return localStorage.getItem(KEY_ACTIVE_MENU) || 'stats';
    } catch (e) {
        return 'stats';
    }
}

export function setActiveMenu(menu) {
    try {
        localStorage.setItem(KEY_ACTIVE_MENU, menu);
    } catch (e) {
        console.warn('setActiveMenu failed:', e);
    }
}

/**
 * Load a build entry into { build, buildName, genConfig } for context
 */
export function loadBuildEntry(entry) {
    if (!entry) return null;
    const build = new Build(entry.build);
    const buildName = entry.name || DEFAULT_NAME;
    const genConfig = new GenConfig(entry.genConfig || {});
    return { build, buildName, genConfig };
}

export { buildToStorage };
